if (!customElements.get("shop-the-look")) {
  class ShopTheLook extends HTMLElement {
    constructor() {
      super();

      this.hotspots = [];
      this.cards = [];
      this.variantSelectors = [];
      this.addAll = null;
      this.totalPriceElement = null;
      this.cartDrawer = null;

      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";

      this.cartErrorEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) ||
        "cart-error";

      this.onHotspotEnter = this.onHotspotEnter.bind(this);
      this.onHotspotLeave = this.onHotspotLeave.bind(this);
      this.onHotspotClick = this.onHotspotClick.bind(this);
      this.onCardEnter = this.onCardEnter.bind(this);
      this.onCardLeave = this.onCardLeave.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onAddAll = this.onAddAll.bind(this);
    }

    connectedCallback() {
      this.hotspots = Array.from(
        this.querySelectorAll("[data-hotspot]")
      );

      this.cards = Array.from(
        this.querySelectorAll("[data-hotspot-card]")
      );

      this.variantSelectors = Array.from(
        this.querySelectorAll("[data-variant-select]")
      );

      this.addAll = this.querySelector("[data-add-all]");

      this.totalPriceElement = this.querySelector(
        "[data-total-price]"
      );

      this.hotspots.forEach((spot) => {
        spot.addEventListener("mouseenter", this.onHotspotEnter);
        spot.addEventListener("mouseleave", this.onHotspotLeave);
        spot.addEventListener("focus", this.onHotspotEnter);
        spot.addEventListener("blur", this.onHotspotLeave);
        spot.addEventListener("click", this.onHotspotClick);
      });

      this.cards.forEach((card) => {
        card.addEventListener("mouseenter", this.onCardEnter);
        card.addEventListener("mouseleave", this.onCardLeave);
      });

      this.variantSelectors.forEach((selector) => {
        selector.addEventListener("change", this.onVariantChange);
      });

      if (this.addAll) {
        this.addAll.addEventListener("click", this.onAddAll);
      }

      this.updateSummary();
    }

    disconnectedCallback() {
      this.hotspots.forEach((spot) => {
        spot.removeEventListener("mouseenter", this.onHotspotEnter);
        spot.removeEventListener("mouseleave", this.onHotspotLeave);
        spot.removeEventListener("focus", this.onHotspotEnter);
        spot.removeEventListener("blur", this.onHotspotLeave);
        spot.removeEventListener("click", this.onHotspotClick);
      });

      this.cards.forEach((card) => {
        card.removeEventListener("mouseenter", this.onCardEnter);
        card.removeEventListener("mouseleave", this.onCardLeave);
      });

      this.variantSelectors.forEach((selector) => {
        selector.removeEventListener("change", this.onVariantChange);
      });

      if (this.addAll) {
        this.addAll.removeEventListener("click", this.onAddAll);
      }
    }

    setActive(id) {
      this.hotspots.forEach((spot) => {
        const active = spot.dataset.hotspotId === id;

        spot.dataset.active = active ? "true" : "false";

        const ring = spot.querySelector("[data-hotspot-ring]");
        const dot = spot.querySelector("[data-hotspot-dot]");

        if (ring) {
          ring.classList.toggle("opacity-100", active);
          ring.classList.toggle("scale-100", active);
          ring.classList.toggle("opacity-0", !active);
          ring.classList.toggle("scale-50", !active);
        }

        if (dot) {
          dot.classList.toggle("scale-125", active);
          dot.classList.toggle("bg-accent", active);
          dot.classList.toggle("text-accent_contrast", active);
        }
      });

      this.cards.forEach((card) => {
        const active = card.dataset.hotspotId === id;

        card.dataset.active = active ? "true" : "false";

        card.classList.toggle("border-accent", active);
        card.classList.toggle("bg-accent/5", active);
        card.classList.toggle("border-foreground/5", !active);
        card.classList.toggle("bg-background", !active);
      });
    }

    onHotspotEnter(event) {
      const id =
        event.currentTarget?.dataset?.hotspotId || "";

      this.setActive(id);
    }

    onHotspotLeave() {
      this.setActive("");
    }

    onHotspotClick(event) {
      const id =
        event.currentTarget?.dataset?.hotspotId;

      if (!id) return;

      const target = this.querySelector(
        `#Shop-The-Look-Item-${CSS.escape(id)}`
      );

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }

    onCardEnter(event) {
      const id =
        event.currentTarget?.dataset?.hotspotId || "";

      this.setActive(id);
    }

    onCardLeave() {
      this.setActive("");
    }

    onVariantChange(event) {
      const selector = event.currentTarget;
      const selectedOption =
        selector.options[selector.selectedIndex];

      const card = selector.closest(
        "[data-hotspot-card]"
      );

      if (!card || !selectedOption) return;

      const variantId = selectedOption.value;
      const price = Number(
        selectedOption.dataset.price || 0
      );
      const compareAtPrice = Number(
        selectedOption.dataset.compareAtPrice || 0
      );
      const available =
        selectedOption.dataset.available === "true";

      card.dataset.variantId = variantId;
      card.dataset.price = String(price);
      card.dataset.compareAtPrice =
        String(compareAtPrice);
      card.dataset.available = String(available);

      this.updateCardPrice(card, {
        price,
        compareAtPrice,
        available
      });

      this.updateSummary();
    }

    updateCardPrice(
      card,
      {
        price = 0,
        compareAtPrice = 0,
        available = false
      } = {}
    ) {
      const priceElement = card.querySelector(
        "[data-card-price]"
      );

      const compareAtPriceElement = card.querySelector(
        "[data-card-compare-price]"
      );

      const discountWrapper = card.querySelector(
        "[data-discount-wrapper]"
      );

      const discountedPriceElement = card.querySelector(
        "[data-discounted-price]"
      );

      const soldOutMessage = card.querySelector(
        "[data-sold-out-message]"
      );

      if (priceElement) {
        priceElement.textContent =
          this.formatMoney(price);
      }

      if (compareAtPriceElement) {
        const showCompareAtPrice =
          compareAtPrice > price;

        compareAtPriceElement.textContent =
          showCompareAtPrice
            ? this.formatMoney(compareAtPrice)
            : "";

        compareAtPriceElement.classList.toggle(
          "hidden",
          !showCompareAtPrice
        );
      }

      if (
        discountWrapper &&
        discountedPriceElement
      ) {
        const discountPercentage = Number(
          discountWrapper.dataset
            .discountPercentage || 0
        );

        const multiplier =
          (100 - discountPercentage) / 100;

        const discountedPrice = Math.round(
          price * multiplier
        );

        discountedPriceElement.textContent =
          this.formatMoney(discountedPrice);
      }

      if (soldOutMessage) {
        soldOutMessage.classList.toggle(
          "hidden",
          available
        );
      }
    }

    getAvailableItems() {
      return this.cards
        .map((card) => {
          const variantId = card.dataset.variantId;
          const price = Number(
            card.dataset.price || 0
          );
          const available =
            card.dataset.available === "true";

          return {
            id: variantId,
            quantity: 1,
            price,
            available
          };
        })
        .filter((item) => {
          return Boolean(item.id) && item.available;
        });
    }

    updateSummary() {
      const items = this.getAvailableItems();

      const totalPrice = items.reduce(
        (total, item) => total + item.price,
        0
      );

      this.variants = items.map(
        (item) => item.id
      );

      this.dataset.variants =
        this.variants.join(",");

      if (this.totalPriceElement) {
        this.totalPriceElement.textContent =
          this.formatMoney(totalPrice);
      }

      this.updateAddAllAvailability();
    }

    updateAddAllAvailability() {
      if (!this.addAll) return;

      const isLoading =
        this.addAll.dataset.state === "loading";

      const hasAvailableItems =
        this.variants.length > 0;

      const disabled =
        isLoading || !hasAvailableItems;

      this.addAll.disabled = disabled;
      this.addAll.setAttribute(
        "aria-disabled",
        String(disabled)
      );
    }

    setAddAllState(state = "idle") {
      if (!this.addAll) return;

      const idle = this.addAll.querySelector(
        "[data-add-all-idle]"
      );

      const loading = this.addAll.querySelector(
        "[data-add-all-loading]"
      );

      const isLoading = state === "loading";

      this.addAll.dataset.state = state;

      if (isLoading) {
        this.addAll.setAttribute(
          "aria-busy",
          "true"
        );
      } else {
        this.addAll.removeAttribute(
          "aria-busy"
        );
      }

      if (idle) {
        idle.classList.toggle(
          "opacity-0",
          isLoading
        );

        idle.classList.toggle(
          "-translate-y-4",
          isLoading
        );
      }

      if (loading) {
        loading.classList.toggle(
          "opacity-0",
          !isLoading
        );

        loading.classList.toggle(
          "translate-y-4",
          !isLoading
        );
      }

      this.updateAddAllAvailability();
    }

    getSectionsToRender() {
      const drawer =
        document.getElementById("Cart-Drawer");

      const drawerSectionId =
        drawer?.dataset?.sectionId ||
        "cart-drawer";

      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section"
        },
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
          selector:
            "[data-cart-drawer-content]"
        }
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach(
        (section) => {
          const html =
            sections[section.section];

          if (!html) return;

          const container =
            document.getElementById(
              section.id
            );

          if (!container) return;

          if (!section.selector) {
            container.innerHTML = html;
            return;
          }

          const parsed = new DOMParser()
            .parseFromString(
              html,
              "text/html"
            );

          const element =
            parsed.querySelector(
              section.selector
            );

          if (!element) {
            container.innerHTML = html;
            return;
          }

          if (
            section.id === "Cart-Drawer"
          ) {
            const contentTarget =
              container.querySelector(
                "[data-cart-drawer-content]"
              );

            const summaryTarget =
              container.querySelector(
                "[data-cart-drawer-summary]"
              );

            const progressTarget =
              container.querySelector(
                "free-shipping-progress"
              );

            if (contentTarget) {
              contentTarget.innerHTML =
                element.innerHTML;

              if (
                window.webvista
                  ?.initLazyImageAnimation
              ) {
                window.webvista
                  .initLazyImageAnimation(
                    contentTarget
                  );
              }
            }

            const summaryElement =
              parsed.querySelector(
                "[data-cart-drawer-summary]"
              );

            if (
              summaryTarget &&
              summaryElement
            ) {
              summaryTarget.innerHTML =
                summaryElement.innerHTML;

              summaryTarget.className =
                summaryElement.className;
            }

            const progressSource =
              parsed.querySelector(
                "free-shipping-progress"
              );

            if (
              progressTarget &&
              progressSource
            ) {
              progressTarget.dataset
                .totalAmount =
                progressSource.dataset
                  .totalAmount || "";
            }

            if (
              typeof container
                .refreshUpsell === "function"
            ) {
              container.refreshUpsell();
            }

            return;
          }

          container.innerHTML =
            element.innerHTML;
        }
      );
    }

    async openCartDrawer(opener) {
      const header =
        document.querySelector(
          "header-component"
        );

      const cartType =
        header?.dataset.cartType ||
        "drawer";

      const cartOpenOnAdd =
        header?.dataset.cartOpenOnAdd !==
        "false";

      if (!cartOpenOnAdd) return;

      if (cartType === "page") {
        window.location.href =
          window.routes?.cart_url ||
          "/cart";

        return;
      }

      if (!this.cartDrawer) {
        this.cartDrawer =
          document.getElementById(
            "Cart-Drawer"
          );
      }

      if (!this.cartDrawer) return;

      if (
        typeof this.cartDrawer.show ===
        "function"
      ) {
        this.cartDrawer.show(opener);
        return;
      }

      await customElements.whenDefined(
        "cart-drawer"
      );

      if (
        typeof this.cartDrawer.show ===
        "function"
      ) {
        this.cartDrawer.show(opener);
      }
    }

    async onAddAll() {
      const availableItems =
        this.getAvailableItems();

      if (
        !availableItems.length ||
        !this.addAll
      ) {
        return;
      }

      const items = availableItems.map(
        (item) => ({
          id: item.id,
          quantity: item.quantity
        })
      );

      const sections =
        this.getSectionsToRender().map(
          (section) => section.section
        );

      const cartAddUrl =
        window.routes?.cart_add_url ||
        `${
          window.Shopify?.routes?.root ||
          "/"
        }cart/add.js`;

      try {
        this.setAddAllState("loading");

        const response = await fetch(
          cartAddUrl,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url:
                window.location.pathname
            })
          }
        );

        const data =
          await response.json();

        if (!response.ok) {
          document.dispatchEvent(
            new CustomEvent(
              this.cartErrorEvent,
              {
                detail: {
                  error: data
                }
              }
            )
          );

          throw new Error(
            data?.description ||
              data?.message ||
              "Add all failed"
          );
        }

        if (data?.sections) {
          this.renderSections(
            data.sections
          );
        }

        await this.openCartDrawer(
          this.addAll
        );

        const firstVariantId =
          this.variants[0];

        document.dispatchEvent(
          new CustomEvent(
            this.cartUpdateEvent,
            {
              detail: {
                variantId:
                  firstVariantId,
                items,
                cart: data,
                source:
                  "shop-the-look"
              }
            }
          )
        );
      } catch (error) {
        console.error(
          "Shop the Look:",
          error
        );
      } finally {
        this.setAddAllState("idle");
      }
    }

    formatMoney(cents) {
      const numericCents =
        Number(cents) || 0;

      if (
        window.Shopify &&
        typeof window.Shopify
          .formatMoney === "function"
      ) {
        return window.Shopify.formatMoney(
          numericCents
        );
      }

      if (
        window.Currency &&
        typeof window.Currency
          .formatMoney === "function"
      ) {
        return window.Currency.formatMoney(
          numericCents
        );
      }

      const currency =
        window.Shopify?.currency
          ?.active || "EUR";

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang ||
            "el-GR",
          {
            style: "currency",
            currency
          }
        ).format(numericCents / 100);
      } catch (error) {
        return `${(
          numericCents / 100
        ).toFixed(2)} €`;
      }
    }
  }

  customElements.define(
    "shop-the-look",
    ShopTheLook
  );
}